import diacritics from 'diacritics';
import { BaseUser, WebRequestStatus } from '@/models';

export const sleep = (ms: number) => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

export const atLeast = async <T>(
  promise: Promise<T>,
  durationInMs = 1000
): Promise<T> => {
  const [t] = await Promise.all([promise, sleep(durationInMs)]);

  return t;
};

export async function makeWebRequest<T>(
  request: () => Promise<T>,
  processFn: (result: T) => void,
  statusUpdateFn: (status: WebRequestStatus) => void
) {
  statusUpdateFn(WebRequestStatus.IN_PROGRESS);

  try {
    const result = await request();

    processFn(result);

    statusUpdateFn(WebRequestStatus.SUCCESS);

    await sleep(1000);
  } catch (error) {
    console.error(error);

    statusUpdateFn(WebRequestStatus.FAILURE);
    await sleep(4000);
  }
  statusUpdateFn(WebRequestStatus.READY);
}

export const updateFirstWhere = <T>(
  items: T[],
  matchFn: (item: T) => boolean,
  updateFn: (item: T) => T
) => {
  const index = items.findIndex(matchFn);
  const updatedItem = updateFn(items[index]);

  return Object.assign([], items, { [index]: updatedItem });
};

export const sortByString = <T>(
  a: T,
  b: T,
  accessorFn: (o: T) => string,
  reverse = false
) => {
  const aField = accessorFn(a).toLowerCase();
  const bField = accessorFn(b).toLowerCase();

  const ascendingValue = aField < bField ? -1 : aField > bField ? 1 : 0;

  return reverse ? -ascendingValue : ascendingValue;
};

interface Named {
  name: string;
}

export const sortNamed = <T extends Named>(a: T, b: T, reverse = false) => {
  return sortByString(a, b, o => o.name, reverse);
};

export const sortUsers = <T extends BaseUser>(a: T, b: T) => {
  return sortByString(a, b, u => u.name || u.email);
};

// From: https://stackoverflow.com/a/9204568
export const validateEmail = (email: string): boolean => {
  if (/^\S+@\S+\.\S+$/.test(email)) {
    return true;
  }
  return false;
};

export const parseByDelimiter = (text: string): string[] => {
  return text.split(/[\s;,\t]/).filter(s => !!s);
};

export const trimBrackets = (text: string): string => {
  return text.replace(/^[<[{(]/, '').replace(/[>\]})]/, '');
};

/**
 * Unfortunately, something like `class RequestTracker<T extends number | string>{}`
 * doesn't work here.
 *
 * More at https://stackoverflow.com/questions/49242232/constraining-type-in-typescript-generic-to-be-one-of-several-types
 */
export class RequestTrackerById {
  constructor(
    private requests: { [key: number]: WebRequestStatus },
    private defaultStatus = WebRequestStatus.READY
  ) {}

  setStatus(id: number, status: WebRequestStatus): RequestTrackerById {
    return new RequestTrackerById({
      ...this.requests,
      [id]: status
    });
  }

  getStatus(id: number) {
    return this.requests[id] ?? this.defaultStatus;
  }
}

export class RequestTrackerByKey {
  constructor(
    private requests: { [key: string]: WebRequestStatus },
    private defaultStatus = WebRequestStatus.READY
  ) {}

  setStatus(key: string, status: WebRequestStatus): RequestTrackerByKey {
    return new RequestTrackerByKey({
      ...this.requests,
      [key]: status
    });
  }

  getStatus(key: string) {
    return this.requests[key] ?? this.defaultStatus;
  }
}

// Use negative numbers to avoid collisions
export const idGenerator = (() => {
  let id = -1;

  return { next: () => id-- };
})();

const normalize = (s: string): string => diacritics.remove(s.toLowerCase());

export const findTerm = <T>(
  term: string,
  corpus: T[],
  accessFn: (t: T) => (string | undefined)[]
): T[] => {
  const normalizedTerm = normalize(term);

  return corpus.filter(c =>
    accessFn(c).some(field => normalize(field || '').includes(normalizedTerm))
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const log = (obj: any) => {
  if (process.env.NODE_ENV === 'production') {
    return;
  } else {
    console.log(obj);
  }
};
